import React from 'react'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'

import TextContent from '../ui/TextContent/TextContent'
import ImageContainer from '../ui/ImageContainer/ImageContainer'
import BulletContents from '../ui/BulletContents/BulletContents'
import HtmlContent from '../ui/HtmlContent/HtmlContent'
import MissionCard from '../MissionCard/MissionCard'
import Image from '../ui/Image/Image'
import Markdown from '../Markdown/Markdown'
import { getImageUrl, isLanguage } from '../../utils'
import { useLanguage } from '../../context/LangaugeContextProvider/LangaugeContextProvider'

import styles from './MissionVisionLayout.module.scss'
import arrowUpRight from '../../assets/images/arrowUpRight.svg'

const MissionVisionLayout = ({ data = {} }) => {
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { isSpanish, isFrench } = isLanguage(language?.code)

  const buttonClickHandler = (path) => {
    navigate(path)
  }

  return (
    <div>
      {/* Section 1 */}
      <TextContent
        title={data['section1']?.title}
        titleClass={classNames(styles['intro-title'])}
        containerClass={classNames(
          styles['intro-container'],
          styles['section1-container']
        )}
      >
        <div className={classNames(styles['intro-description'])}>
          {data['section1']?.description}
        </div>
      </TextContent>

      {/* Section 2 */}
      <div
        className={classNames(
          styles['section2-container'],
          styles['grid-cols-custom'],
          'grid gap-y-2 gap-x-8'
        )}
      >
        <TextContent
          title={data['section2']?.title}
          titleClass={classNames(
            styles['title'],
            isFrench ? styles['fr_title'] : ''
          )}
          containerClass={classNames(styles['card-container'])}
        >
          <HtmlContent
            content={data['section2']?.description}
            contentClass={classNames(styles['description'])}
          />
        </TextContent>
        <div
          className={classNames(
            styles['right-column'],
            'grid grid-cols-2 md:grid-cols-1 gap-x-2 gap-y-8'
          )}
        >
          {data['section2']?.image1 && (
            <ImageContainer
              imageUrl={getImageUrl(data['section2']?.image1)}
              imageClass={styles['globe-image']}
            />
          )}
          {data['section2']?.image2 && (
            <ImageContainer imageUrl={getImageUrl(data['section2']?.image2)} />
          )}
        </div>
      </div>

      {/* Section 3 */}
      <div className={classNames(styles['full-bg-container'])}>
        <div className={classNames(styles['full-bg-inner-container'])}>
          <TextContent
            title={data['section3']?.title}
            titleClass={classNames(
              styles['title'],
              styles['left-column'],
              isFrench ? styles['fr_title'] : ''
            )}
            containerClass={styles.leftContainer}
          >
            {data['section3']?.digitalpaymentimage && (
              <Image
                src={getImageUrl(data['section3']?.digitalpaymentimage)}
                className={styles.sectionTextImage}
                alt={data['section3']?.digitalpaymentimage?.alternativeText}
              />
            )}
          </TextContent>
          <Markdown
            content={data['section3']?.description}
            className={classNames(
              styles['description'],
              styles['right-column'],
              styles['mt-11']
            )}
          />
        </div>
      </div>

      {/* Section 4 */}

      <div className={classNames(styles['section4-container'])}>
        <TextContent
          title={data['section4']?.title}
          titleClass={classNames(
            styles['title'],
            'text-center mb-6 md:mb-10',
            isSpanish ? styles['es_title'] : ''
          )}
          containerClass={classNames(
            styles['intro-container'],
            styles['section4-title-container'],
            'gap-0'
          )}
        >
          <HtmlContent
            content={data['section4']?.description}
            contentClass={classNames(
              styles['secondary-description']
              // styles['section4-description']
            )}
          />
        </TextContent>
        <div
          className={classNames(
            'grid grid-cols-1 md:grid-cols-3 gap-y-3 gap-x-10 mt-10 md:mt-20'
          )}
        >
          {data['section4']?.card?.map((card) => {
            return (
              <MissionCard
                key={'missioncard' + card?.id}
                imageUrl={getImageUrl(card?.image)}
                title={card?.title}
                description={card?.description}
                linkTitle={card?.link?.label}
                linkPath={card?.link?.path}
                alternativeText={card?.image?.alternativeText}
              />
            )
          })}
        </div>
      </div>

      {/* Section 5 */}
      <div className={classNames(styles['full-bg-container'])}>
        <div className={classNames(styles['full-bg-inner-container'])}>
          <TextContent
            title={data['section5']?.title}
            titleClass={classNames(
              styles['title'],
              styles['left-column'],
              isFrench ? styles['fr_title'] : ''
            )}
            containerClass={classNames(styles.leftContainer)}
          >
            {data['section5']?.digitaltransformImage && (
              <Image
                src={getImageUrl(data['section5']?.digitaltransformImage)}
                className={styles.sectionTextImage}
                alt={data['section5']?.digitaltransformImage?.alternativeText}
              />
            )}
          </TextContent>
          <div
            className={classNames(
              styles['description'],
              styles['right-column'],
              styles['mt-11'],
              'flex flex-column gap-6 md:gap-10 md:mt'
            )}
          >
            {data['section5']?.subsection?.map((section) => {
              return (
                <BulletContents
                  key={'bulletcontent' + section?.id}
                  title={section?.title}
                  description={section?.description}
                  textContainerClass={styles.textContainerClass}
                />
              )
            })}
          </div>
        </div>
      </div>

      {/* Section 6 */}
      <div className={classNames(styles['section6-container'])}>
        <TextContent
          title={data['section6']?.title}
          titleClass={classNames(styles['intro-title'])}
          containerClass={classNames(
            styles['intro-container'],
            styles['section6-inner-container']
          )}
        >
          <div className={classNames(styles['intro-description'])}>
            {data['section6']?.description}
          </div>
        </TextContent>
      </div>

      {/* Section 7*/}
      {data?.section7 && (
        <div className={classNames(styles['section7-container'])}>
          <div className={classNames(styles.topContainer)}>
            <div className={classNames(styles.textContainer)}>
              <h2>{data?.section7?.largetitle}</h2>
              {data?.section7?.smalltitle && (
                <span>{data?.section7?.smalltitle}</span>
              )}
            </div>
            <div className={styles.yearContainer}>
              <span>{data?.section7?.year1}</span>
              <button
                aria-label={data?.section7?.menu_link?.label}
                className={styles.navigationButton}
                onClick={() => {
                  // buttonClickHandler(data?.section7?.menu_link?.path)
                }}
              >
                <Image
                  src={arrowUpRight}
                  alt={'go to milestone'}
                  className={styles.navigationIcon}
                />
              </button>
              <span>{data?.section7?.year2}</span>
            </div>
          </div>
          <img
            src={getImageUrl(data?.section7?.image)}
            alt={data?.section7?.image?.alternativeText}
            className={styles.worldImage}
          />
        </div>
      )}
    </div>
  )
}

export default MissionVisionLayout
